import {ChangeDetectionStrategy, Component, computed, input, output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgSelectComponent} from '@ng-select/ng-select';
import {TradeBusinessOptionDto} from '../../../models/generated-models';

/**
 * Business picker shared by the trade pages. Renders nothing when the user has
 * a single business — that one is auto-selected by the store.
 */
@Component({
  selector: 'app-trade-business-select',
  standalone: true,
  imports: [FormsModule, NgSelectComponent],
  template: `
    @if (businesses().length > 1) {
      <div [class.mb-3]="!inline()"
           [class.d-flex]="inline()"
           [class.align-items-center]="inline()"
           [class.gap-2]="inline()"
           class="trade-business-select-wrap">
        <label class="form-label"
               [class.mb-0]="inline()"
               [class.text-nowrap]="inline()"
               [attr.for]="inputId()">{{ label() }}</label>
        <ng-select
          [class.flex-grow-1]="inline()"
          [labelForId]="inputId()"
          [items]="options()"
          bindValue="id"
          bindLabel="label"
          [clearable]="false"
          [searchable]="true"
          [ngModel]="selectedId()"
          (ngModelChange)="selectedIdChange.emit($event ?? null)"
          placeholder="เลือกธุรกิจ"
          notFoundText="ไม่พบธุรกิจ">
        </ng-select>
      </div>
    }
  `,
  styles: `
    :host {
      display: block;
    }
    .trade-business-select-wrap {
      width: 100%;
    }
    .trade-business-select-wrap.d-flex {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      gap: 0.5rem;
    }
    .trade-business-select-wrap.d-flex ng-select {
      flex: 1 1 auto;
      min-width: 0;
    }
    ng-select {
      width: 100%;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeBusinessSelectComponent {
  readonly businesses = input.required<TradeBusinessOptionDto[]>();
  /** Several businesses may share a title (e.g. two farms of one owner); the tax id tells them apart. */
  protected readonly options = computed(() => {
    const items = this.businesses();
    const titleCount = new Map<string, number>();
    for (const item of items) {
      const key = (item.title ?? '').trim();
      titleCount.set(key, (titleCount.get(key) ?? 0) + 1);
    }
    return items.map((item) => {
      const title = (item.title ?? '').trim();
      const duplicate = (titleCount.get(title) ?? 0) > 1;
      const suffix = duplicate ? (item.taxId ? ` (${item.taxId})` : ` (#${item.id})`) : '';
      return {...item, label: `${title}${suffix}`};
    });
  });
  readonly selectedId = input<number | null>(null);
  readonly label = input('ธุรกิจ');
  readonly inputId = input('trade-business-select');
  readonly inline = input(false);
  readonly selectedIdChange = output<number | null>();
}
